import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import cx from 'classnames'
import _ from 'lodash'
import { useContext, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import compiler from 'shared_src/igao/compiler'
import './main.scss'

import { PatientIdContext } from '../../lib/context/patient-id'
import igao from '../../lib/igao'
import { CircularProgress } from '../shared/NarusMaterial'

import { DynamicIGAO } from './components/dynamic-igao'
import { discard, publish, read, resolve, sendDraft } from './controller'
import styles from './index.module.scss'
import dataMock from './mock.json'
import tasksDataMock from './tasks-mock.json'
import { cancelledOrCompleted, favorable, staticType, useStateCallback } from './utils'

function scrollIntoView (element, offset = 100) {
  if (!element) {
    return
  }

  let wrapper = document.querySelector('.s-content')
  let banner = document.querySelector('.patient-banner')

  if (!wrapper || !banner) {
    return
  }

  let top = Math.max(
    0,
    element.getBoundingClientRect().top + wrapper.scrollTop - banner.getBoundingClientRect().height - offset
  )

  wrapper.scrollTo({
    top,
    behavior: 'smooth'
  })
}

export function IGAO ({ path, withTopMargin = true, disabled = false, error = null }) {
  let [ searchParams ] = useSearchParams()
  let queryClient = useQueryClient()
  let scrollToItemID = searchParams.get('igao-item-id')
  let [ draft, setDraft ] = useState(null)
  let [ data, setData ] = useStateCallback(null)
  let dispatch = useDispatch()
  let resource = useContext(PatientIdContext)
  const otherTypesOfIGAO = [ 'tasks', 'careplan' ]

  useQuery({
    queryKey: [ 'igao', path, resource ],
    queryFn: async () => {
      if (otherTypesOfIGAO.includes(path)) {
        return new Promise((resolve) => resolve({ data: path === 'careplan' ? dataMock : tasksDataMock }))
      }

      if (!resource) {
        return
      }

      try {
        setDraft({ getting: true })
        const result = await igao.get({ resource, path })
        setDraft({
          get: true,
          data: result.data
        })
        setData(result.data)
      } catch (e) {
        setDraft({ e })
      }
    },
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData
  })

  useEffect(() => {
    setTimeout(() => {
      scrollIntoView(document.querySelector(`#${scrollToItemID}`))
    }, 500)

    return () => {
      setDraft(null)
      setData(null)
    }
  }, [ setData, setDraft, scrollToItemID ])

  const setAnswer = item => {
    if (item.answer === favorable(item.unfavorable) || item.type === 'plain') return item.answer
    return Object.prototype.hasOwnProperty.call(item, 'issues') &&
    item.issues.some(i => !cancelledOrCompleted(i))
      ? item.unfavorable
      : null
  }

  const recalculateIssues = tempData =>
    tempData[path].items.map(i => {
      const item = { ...i }
      item.answer = setAnswer(item)
      if (staticType.includes(i.type)) return item
      if (!i.issues) return item
      item.issues = i.issues.map(_issue => {
        const issue = { ..._issue }
        issue.urgent =
          !cancelledOrCompleted(issue) &&
          issue?.actions?.some(
            act => act.urgent && !cancelledOrCompleted(act)
          )
        return issue
      })
      return item
    })

  const set = value => {
    setDraft({ setting: true })
    const state = current => {
      const proposed = _.isFunction(value)
        ? value(_.cloneDeep(current))
        : _.cloneDeep(value)

      return compiler.compile({
        current: { data: current },
        proposed: { data: proposed }
      }).data
    }
    setData(state, async valueData => {
      const tempData = _.cloneDeep(valueData)
      tempData[path].items = recalculateIssues(valueData)
      await sendDraft(resource, path, tempData, setData, setDraft)
      await queryClient.invalidateQueries({ queryKey: [ 'drafts', resource ] })
    })
  }

  const handleResolve = () => {
    setData(valueData => {
      const tempData = _.cloneDeep(valueData)
      tempData[path].items = recalculateIssues(valueData)
      resolve(resource, path, tempData, setData, setDraft)
      return valueData
    })
  }

  const handlePublish = async () => {
    setDraft({ publishing: true })
    await publish(resource, path, setData, setDraft, dispatch, data)
    await queryClient.invalidateQueries({ queryKey: [ 'drafts', resource ] })
  }

  async function handleDiscard () {
    await discard(resource, path, setData, setDraft)
    await queryClient.invalidateQueries({ queryKey: [ 'drafts', resource ] })
  }

  if (!otherTypesOfIGAO.includes(path) && !resource) {
    return (
      <div
        className={cx(styles.igaoRoot, {
          [styles.mt]: withTopMargin
        })}
      >
        {
          typeof draft?.error === 'string'
            ? draft.error
            : typeof draft?.error?.message === 'string'
              ? draft.error.message
              : `No user ID (${typeof resource})`
        }
      </div>
    )
  }

  if (draft?.getting) {
    return <CircularProgress />
  }

  if (!data?.[path]) {
    return (
      <div
        className={cx(styles.igaoRoot, {
          [styles.mt]: withTopMargin
        })}
      >
        {
          typeof draft?.error === 'string'
            ? draft.error
            : typeof draft?.error?.message === 'string'
              ? draft.error.message
              : `No IGAO schema for ${path}`
        }
      </div>
    )
  }

  return (
    <div className={cx(styles.igaoRoot, { [styles.mt]: withTopMargin })}>
      <DynamicIGAO
        data={data}
        setPageData={set}
        page={path}
        draft={draft}
        discard={handleDiscard}
        publish={handlePublish}
        resolve={handleResolve}
        readComments={read(resource, path, setData)}
        disabled={disabled}
        error={error}
      />
    </div>
  )
}
